import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Button } from 'react-bootstrap';
import useStartAttendance from '../../../hooks/useStartAttendance';
import { toMinutes, formatMinutes, timeRange, roomLabel } from '../../../utils/lessonLabels';
import './TodayClassesPanel.css';

// Today's lessons as the classes the professor walks into: lessons in the same room whose times
// overlap the first one are taught together and get one QR, as the server allows. Lessons a QR has
// already combined today (same classKey) are one class whatever their room: the server locks them.
// Spec: ops/combined-classes-qr-spec.md
//
// A room is its building and code, not its record: P2 304 exists twice in UMS, and on 2026-10-15
// that split one class into two here. The Online rooms (code "0", one per building) are one.
const roomKeyOf = (lesson) => {
  const code = (lesson.roomCode ?? '').trim().toLowerCase();
  if (code === '0') return 'online';
  return code ? `${lesson.branchName ?? ''}/${code}` : `id:${lesson.roomId ?? ''}`;
};

const groupIntoClasses = (lessons) => {
  const classes = [];
  [...lessons]
    .sort((a, b) => (toMinutes(a.startTime) ?? 0) - (toMinutes(b.startTime) ?? 0))
    .forEach(lesson => {
      const start = toMinutes(lesson.startTime);
      const end = toMinutes(lesson.endTime);
      const roomKey = roomKeyOf(lesson);
      const classKey = lesson.classKey ?? null;
      const together = (classKey !== null && classes.find(c => c.classKey === classKey))
        || (start !== null && end !== null && classes.find(c =>
          c.roomKey === roomKey && c.start !== null && c.end !== null && start < c.end && c.start < end
          && (classKey === null || c.classKey === null || c.classKey === classKey)));
      if (together) {
        together.lessons.push(lesson);
        if (together.classKey === null) together.classKey = classKey;
      } else {
        classes.push({ roomKey, start, end, classKey, lessons: [lesson] });
      }
    });
  return classes;
};

// Whether a QR can be opened now, by the windows the server applies (SessionTiming).
const qrWindow = (minutes, before, after, nowMinutes) => {
  if (minutes === null) return { open: true };
  if (nowMinutes < minutes - before) return { open: false, opensAt: formatMinutes(minutes - before) };
  if (nowMinutes > minutes + after) return { open: false, closedAt: formatMinutes(minutes + after) };
  return { open: true };
};

const TodayClassesPanel = ({ lessons, attendanceWindow }) => {
  const { t } = useTranslation();
  const { openQr } = useStartAttendance();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const ticker = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(ticker);
  }, []);

  const classes = groupIntoClasses(lessons || []);
  if (!classes.length) return null;

  const nowMinutes = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  const windows = {
    startMinutesBefore: 10, startMinutesAfter: 40, endMinutesBefore: 15, endMinutesAfter: 20,
    ...attendanceWindow
  };
  const hint = (qr) =>
    qr.opensAt ? t('qrOpensAt', { time: qr.opensAt }) : qr.closedAt ? t('qrClosedAt', { time: qr.closedAt }) : undefined;

  return (
    <Card className="dashboard-card mb-4">
      <Card.Body>
        <h5 className="card-title mb-3">
          <i className="bi bi-qr-code me-2"></i>
          {t("todaysClasses")}
          <Badge bg="secondary" className="ms-2">{classes.length}</Badge>
        </h5>
        {classes.map(cls => {
          const ids = cls.lessons.map(l => l.schedulePlanWeekId);
          const startQr = qrWindow(cls.start, windows.startMinutesBefore, windows.startMinutesAfter, nowMinutes);
          const endQr = qrWindow(cls.end, windows.endMinutesBefore, windows.endMinutesAfter, nowMinutes);
          const isNow = cls.start !== null && cls.end !== null && nowMinutes >= cls.start && nowMinutes <= cls.end;
          const isOver = !!endQr.closedAt;
          return (
            <div
              key={ids.join('-')}
              className={`today-class d-flex flex-wrap align-items-center gap-3 ${isNow ? 'today-class-now' : ''} ${isOver ? 'today-class-over' : ''}`}
            >
              <div className="today-class-when">
                <div className="fw-bold">{timeRange(cls.lessons[0].startTime, cls.lessons[0].endTime)}</div>
                <div className="small text-muted">{[...new Set(cls.lessons.map(l => roomLabel(l) || t("online")))].join(", ")}</div>
              </div>
              <div className="flex-grow-1">
                {cls.lessons.map(l => (
                  <div key={l.schedulePlanWeekId}>
                    <strong>{l.subjectCode}</strong> <span className="small">{l.subjectName}</span>
                    {(l.groups || l.groupName) && <span className="small text-muted"> · {l.groups || l.groupName}</span>}
                  </div>
                ))}
                {cls.lessons.length > 1 && (
                  <Badge bg="danger" className="mt-1">{t("combinedClass")}</Badge>
                )}
              </div>
              <div className="text-end">
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant={startQr.open ? 'danger' : 'outline-secondary'}
                    disabled={!startQr.open}
                    title={hint(startQr)}
                    onClick={() => openQr(ids, 1)}
                  >
                    <i className="bi bi-play-circle me-1"></i>
                    {t("startQr")}
                  </Button>
                  <Button
                    size="sm"
                    variant={endQr.open ? 'danger' : 'outline-secondary'}
                    disabled={!endQr.open}
                    title={hint(endQr)}
                    onClick={() => openQr(ids, 2)}
                  >
                    <i className="bi bi-stop-circle me-1"></i>
                    {t("endQr")}
                  </Button>
                </div>
                {startQr.opensAt && <div className="small text-muted mt-1">{hint(startQr)}</div>}
              </div>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

export default TodayClassesPanel;
